/* eslint-disable @typescript-eslint/no-explicit-any */
// MessageArea uses dynamic message payloads from the chat worker.
import { ArrowDownIcon, ChevronLeftIcon } from '@heroicons/react/24/outline';
import React from 'react';
import { useSearchParams } from 'react-router';

import ChatMessage from '@/sections/insights/aiAssistant/ChatMessage';
import {
  type FailedMessage,
  failedMessageErrorCopy,
} from '@/sections/insights/aiAssistant/utils/failedMessage';
import { groupMessages } from '@/sections/insights/aiAssistant/utils/groupMessages';
import { imageAttachmentsOf } from '@/sections/insights/aiAssistant/utils/imageAttachments';

import { AgentExecutionCard } from './AgentExecutionCard';
import { AgentIcon, ThinkingDots } from './MessageAreaHelpers';
import { ScreenshotGalleryProvider } from './ScreenshotGallery';

interface MessageAreaProps {
  conversationId: string;
  messages: any[];
  workerPendingMessages: any[];
  isLoadingConversation: boolean;
  failedMessage: FailedMessage | null;
  loadingStatus: string;
  isSubagent: boolean;
  parentConversationId: string | undefined;
  selectedConversation: any;
  showScrollToBottom: boolean;
  scrollContainerRef: React.RefObject<HTMLDivElement | null>;
  messagesEndRef: React.RefObject<HTMLDivElement | null>;
  onScrollToBottom: () => void;
  agentExecutions: Map<string, any>;
  expandedAgentCards: Set<string>;
  toggleAgentCard: (id: string) => void;
  // Context-limit notification actions on the chariot error message. Threaded
  // here because ChatMessage renders the notification inline; the parent owns
  // the URL transitions.
  onCreateConversation?: () => void;
  onCompactConversation?: () => void;
}

export function MessageArea({
  conversationId,
  messages,
  workerPendingMessages,
  isLoadingConversation,
  failedMessage,
  loadingStatus,
  isSubagent,
  parentConversationId,
  selectedConversation,
  showScrollToBottom,
  scrollContainerRef,
  messagesEndRef,
  onScrollToBottom,
  agentExecutions,
  expandedAgentCards,
  toggleAgentCard,
  onCreateConversation,
  onCompactConversation,
}: MessageAreaProps) {
  const [, setSearchParams] = useSearchParams();

  const lastMessage = messages[messages.length - 1];
  const isThinking =
    lastMessage &&
    lastMessage.role !== 'chariot' &&
    lastMessage.role !== 'planner-output';

  // All screenshots in capture order, for the click-through gallery lightbox.
  const screenshots = [...new Set(messages.flatMap(imageAttachmentsOf))];

  return (
    <ScreenshotGalleryProvider screenshots={screenshots}>
      {/* Subagent banner */}
      {isSubagent && parentConversationId && (
        <div className="border-chariot-stroke bg-chariot-brand-primary/5 border-b">
          <div className="flex items-center gap-2 px-4 py-2">
            <button
              onClick={() =>
                setSearchParams(prev => {
                  const p = new URLSearchParams(prev);
                  p.set('conversationId', parentConversationId);
                  return p;
                })
              }
              className="text-chariot-brand-primary hover:text-chariot-brand-primary/80 flex items-center gap-1 text-sm font-medium transition-colors"
            >
              <ChevronLeftIcon className="size-4" />
              Return to parent
            </button>
            <span className="text-chariot-text-tertiary text-xs">
              Viewing subagent
              {selectedConversation?.topic
                ? `: ${selectedConversation.topic}`
                : ''}
            </span>
          </div>
        </div>
      )}

      {/* Scroll to bottom */}
      {showScrollToBottom && (
        <button
          onClick={onScrollToBottom}
          className="bg-chariot-background-primary border-chariot-stroke hover:border-chariot-brand-primary hover:bg-chariot-background-secondary absolute bottom-16 left-1/2 z-10 flex size-8 -translate-x-1/2 items-center justify-center rounded-full border transition-colors"
          title="Scroll to bottom"
        >
          <ArrowDownIcon className="text-chariot-text-secondary size-4" />
        </button>
      )}

      <div
        ref={scrollContainerRef}
        className="flex-1 overflow-x-hidden overflow-y-auto"
      >
        <div
          key={conversationId}
          className="flex min-h-full flex-col justify-end"
        >
          <div className="px-6 pt-4">
            {isLoadingConversation && messages.length === 0 ? (
              <div className="mb-4">
                <div className="flex items-center gap-3">
                  <AgentIcon />
                  <div className="flex items-center gap-3">
                    <ThinkingDots />
                    <span className="text-chariot-text-secondary text-sm">
                      Loading conversation...
                    </span>
                  </div>
                </div>
              </div>
            ) : (
              <div className="space-y-2">
                {groupMessages(messages).map((item, _idx) => {
                  if (item.type === 'message') {
                    const isChariotResponse = item.data.role === 'chariot';
                    const hasAgentExec =
                      conversationId && agentExecutions.has(conversationId);
                    return (
                      <div key={item.data.id || item.data.key}>
                        {isChariotResponse && hasAgentExec && (
                          <div className="mb-4">
                            <AgentExecutionCard
                              agentName={
                                agentExecutions.get(conversationId!)!.agentName
                              }
                              agentTitle={
                                agentExecutions.get(conversationId!)!.agentTitle
                              }
                              status={
                                agentExecutions.get(conversationId!)!.status
                              }
                              toolCount={
                                agentExecutions.get(conversationId!)!.toolCount
                              }
                              tokenCount={
                                agentExecutions.get(conversationId!)!.tokenCount
                              }
                              isExpanded={expandedAgentCards.has(
                                conversationId!
                              )}
                              onToggle={() => toggleAgentCard(conversationId!)}
                              toolCalls={
                                agentExecutions.get(conversationId!)!.toolCalls
                              }
                            />
                          </div>
                        )}
                        <ChatMessage
                          message={item.data}
                          isSubagent={isSubagent}
                          onCreateConversation={onCreateConversation}
                          onCompactConversation={onCompactConversation}
                        />
                      </div>
                    );
                  }
                  if (item.data.call) {
                    return (
                      <ChatMessage
                        key={item.data.call.id || item.data.call.key}
                        message={item.data.call}
                        pairedResponse={item.data.response}
                        isSubagent={isSubagent}
                      />
                    );
                  }
                  if (item.data.response) {
                    return (
                      <ChatMessage
                        key={item.data.response.id || item.data.response.key}
                        message={item.data.response}
                        isSubagent={isSubagent}
                        onCreateConversation={onCreateConversation}
                        onCompactConversation={onCompactConversation}
                      />
                    );
                  }
                  return null;
                })}

                {/* Pending messages */}
                {workerPendingMessages.map(pending => (
                  <div key={pending.id} className="mb-4">
                    <div className="flex items-start gap-3">
                      <div
                        className="bg-chariot-stroke mt-1 w-1 flex-shrink-0 rounded-full"
                        style={{ height: '1.2em' }}
                      />
                      <div className="text-chariot-text-secondary text-[15px] leading-[1.6] font-medium break-words whitespace-pre-wrap">
                        {pending.text}
                        <span className="ml-2 text-xs opacity-50">
                          (sending...)
                        </span>
                      </div>
                    </div>
                  </div>
                ))}

                {/* Failed message */}
                {failedMessage && (
                  <div className="mb-4">
                    <div className="text-chariot-text-secondary text-[15px] leading-[1.6] font-medium break-words whitespace-pre-wrap opacity-50">
                      {failedMessage.text}
                    </div>
                    <div className="text-chariot-text-red text-xs">
                      {failedMessageErrorCopy(failedMessage)}
                    </div>
                  </div>
                )}

                {/* Thinking indicator */}
                {isThinking && (
                  <div className="mb-4">
                    <div className="flex items-center gap-3">
                      <AgentIcon />
                      <div className="flex items-center gap-3">
                        <ThinkingDots />
                        {loadingStatus && (
                          <span className="text-chariot-text-secondary text-sm">
                            {loadingStatus}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                <div ref={messagesEndRef} />
              </div>
            )}
          </div>
        </div>
      </div>
    </ScreenshotGalleryProvider>
  );
}
